import React, { useCallback, useRef, useState } from "react";
import "./BeforeAfterSlider.css";

const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeAlt = "Before cleaning",
  afterAlt = "After cleaning",
  beforeLabel = "Before",
  afterLabel = "After",
}) => {
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const isPlaceholderPair = beforeSrc === afterSrc;
  const sliderRef = useRef(null);

  // Reads directly off the container's own bounding box on every move, so dragging works
  // identically — and reversibly — in both directions, from any starting point.
  const updateFromClientX = useCallback((clientX) => {
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    const ratio = (clientX - rect.left) / rect.width;
    setPosition(Math.min(100, Math.max(0, ratio * 100)));
  }, []);

  const handlePointerDown = (event) => {
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateFromClientX(event.clientX);
  };

  const handlePointerMove = (event) => {
    if (!isDragging) return;
    updateFromClientX(event.clientX);
  };

  const stopDrag = () => setIsDragging(false);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      setPosition((value) => Math.max(0, value - 5));
    } else if (event.key === "ArrowRight") {
      setPosition((value) => Math.min(100, value + 5));
    }
  };

  return (
    <div
      className={`ba-slider${isDragging ? " ba-dragging" : ""}`}
      ref={sliderRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onLostPointerCapture={stopDrag}
    >
      <img className="ba-image ba-image-after" src={afterSrc} alt={afterAlt} draggable="false" />
      <img
        className={`ba-image ba-image-before${isPlaceholderPair ? " ba-image-before--placeholder" : ""}`}
        src={beforeSrc}
        alt={beforeAlt}
        draggable="false"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      />

      <span className="ba-label ba-label-before">{beforeLabel}</span>
      <span className="ba-label ba-label-after">{afterLabel}</span>

      <div
        className="ba-handle"
        style={{ left: `${position}%` }}
        role="slider"
        tabIndex={0}
        aria-label="Drag to compare before and after cleaning"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        onKeyDown={handleKeyDown}
      >
        <span className="ba-handle-grip">⇔</span>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
